import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X } from 'lucide-react';

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 768) setIsOpen(false);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const navItems = [
    { name: 'Accueil',     href: '#home'     },
    { name: 'À propos',    href: '#about'    },
    { name: 'Projets',     href: '#projects' },
    { name: 'Compétences', href: '#skills'   },
    { name: 'Contact',     href: '#contact'  }
  ];

  return (
    <motion.nav
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.5 }}
      className={`fixed top-0 left-0 right-0 w-full z-50 transition-all duration-300 ${
        scrolled
          ? 'bg-white/95 backdrop-blur-md shadow-lg'
          : 'bg-transparent'
      }`}
    >
      {/* Barre principale */}
      <div className="w-full py-4" style={{ paddingLeft: '12px', paddingRight: '12px' }}>
        <div className="flex items-center justify-between w-full min-w-0">

          {/* Logo — taille réduite sur très petits écrans */}
          <motion.a
            href="#home"
            className="gradient-text font-bold flex-shrink-0 min-w-0"
            style={{ fontSize: 'clamp(14px, 4vw, 24px)' }}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            Portfolio
          </motion.a>

          {/* Liens desktop */}
          <div className="hidden md:flex items-center space-x-8">
            {navItems.map((item, index) => (
              <motion.a
                key={item.name}
                href={item.href}
                className={`font-medium transition-colors hover:text-purple-600 ${
                  scrolled ? 'text-gray-800' : 'text-white'
                }`}
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                whileHover={{ scale: 1.1 }}
              >
                {item.name}
              </motion.a>
            ))}
          </div>

          {/* Bouton burger — toujours visible et accessible */}
          <button
            onClick={() => setIsOpen(!isOpen)}
            className={`md:hidden flex-shrink-0 flex items-center justify-center rounded-lg transition-colors
              ${scrolled
                ? 'text-gray-800 hover:bg-gray-100'
                : 'text-white hover:bg-white/10'
              }`}
            style={{
              width:  'clamp(32px, 8vw, 44px)',
              height: 'clamp(32px, 8vw, 44px)',
              minWidth: '32px',   /* ← jamais plus petit que 32px */
              minHeight: '32px',
            }}
            aria-label="Menu"
          >
            {isOpen
              ? <X    style={{ width: 'clamp(18px, 5vw, 24px)', height: 'clamp(18px, 5vw, 24px)' }} />
              : <Menu style={{ width: 'clamp(18px, 5vw, 24px)', height: 'clamp(18px, 5vw, 24px)' }} />
            }
          </button>

        </div>
      </div>

      {/* Menu mobile déroulant */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3, ease: 'easeInOut' }}
            className="md:hidden w-full overflow-hidden"
          >
            <div className={`w-full px-3 py-3 flex flex-col gap-1 ${
              scrolled
                ? 'bg-white shadow-xl'
                : 'bg-black/80 backdrop-blur-md'
            }`}>
              {navItems.map((item, index) => (
                <motion.a
                  key={item.name}
                  href={item.href}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.05 }}
                  className={`block w-full py-3 px-3 rounded-lg font-medium transition-all duration-200 ${
                    scrolled
                      ? 'text-gray-800 hover:text-purple-600 hover:bg-purple-50'
                      : 'text-white hover:text-purple-300 hover:bg-white/10'
                  }`}
                  onClick={() => setIsOpen(false)}
                >
                  {item.name}
                </motion.a>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

    </motion.nav>
  );
};

export default Navbar;